import {
  ANNOTATION_COLORS,
  DEFAULT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_STROKE,
  FONT_SIZES,
  STROKE_WIDTHS,
  AnnotationRuleError,
  annotationAt,
  boundsOf,
  canRedo,
  canUndo,
  clearAnnotations,
  commitAnnotations,
  historyOf,
  moveAnnotation,
  normalizeAnnotation,
  redo,
  undo,
  type AnnotationColorName,
  type AnnotationHistory,
  type AnnotationTool,
  type FontSizeName,
  type ImageAnnotation,
  type StrokeWidthName
} from "../../domain/imaging/image-annotation.js";
import { annotationsToSvgElements } from "../../infrastructure/imaging/annotation-renderer.js";

/**
 * EDITOR DE IMAGEM REUTILIZÁVEL — `<app-image-annotation-editor>`.
 *
 * Um único editor para todos os módulos que usam imagem (RI, END, AVR, RDO,
 * Minuta, Book) — comando, item 2. O componente não conhece nenhum módulo:
 * recebe uma imagem e devolve, por evento, as marcações e a imagem achatada.
 * Quem chamou decide o que persistir e onde, pelo `ImageAnnotationService`.
 *
 *   editor.open(dataUrl, annotations)
 *      → evento "annotation-save"  { annotations, renderedDataUrl }
 *      → evento "annotation-cancel"
 *
 * O desenho na tela usa EXATAMENTE o mesmo renderizador da imagem final
 * (`annotationsToSvgElements`), então o que o usuário vê é o que vai para o
 * documento. Os controles do editor (handles, seleção) ficam num SVG à
 * parte, que nunca entra na imagem achatada.
 *
 * Ponteiro unificado (Pointer Events) para mouse, caneta e toque: nada
 * depende de hover nem de botão direito (item 24).
 */

type Mode = AnnotationTool | "select";

const TOOLS: ReadonlyArray<[Mode, string]> = [
  ["select", "SELECIONAR"],
  ["arrow", "SETA"],
  ["ellipse", "CÍRCULO"],
  ["rect", "RETÂNGULO"],
  ["text", "TEXTO"]
];

interface DraftState {
  annotation: ImageAnnotation;
  kind: "create" | "move" | "resize";
  originX: number;
  originY: number;
}

export class AppImageAnnotationEditor extends HTMLElement {
  private imageDataUrl = "";
  private history: AnnotationHistory = historyOf([]);
  private mode: Mode = "select";
  private color: AnnotationColorName = DEFAULT_COLOR;
  private stroke: StrokeWidthName = DEFAULT_STROKE;
  private fontSize: FontSizeName = DEFAULT_FONT_SIZE;
  private selectedId: string | null = null;
  private draft: DraftState | null = null;
  private zoom = 1;
  private error = "";
  private naturalWidth = 0;
  private naturalHeight = 0;

  /** Abre o editor. `annotations` reabre uma edição anterior (item 17). */
  public open(imageDataUrl: string, annotations: readonly ImageAnnotation[] = []): void {
    this.imageDataUrl = imageDataUrl;
    this.history = historyOf(annotations.map(annotation => ({ ...annotation })));
    this.mode = "select";
    this.selectedId = null;
    this.draft = null;
    this.zoom = 1;
    this.error = "";
    this.measureImage();
    this.render();
  }

  public close(): void {
    this.imageDataUrl = "";
    this.innerHTML = "";
  }

  private get annotations(): readonly ImageAnnotation[] {
    return this.draft ? [...this.history.present.filter(a => a.id !== this.draft!.annotation.id), this.draft.annotation] : this.history.present;
  }

  private get selected(): ImageAnnotation | null {
    return this.history.present.find(annotation => annotation.id === this.selectedId) ?? null;
  }

  /** Dimensões naturais: a imagem achatada é gerada no tamanho real, sem perder resolução (item 26). */
  private measureImage(): void {
    if (!this.imageDataUrl) return;
    const probe = new Image();
    probe.onload = () => {
      this.naturalWidth = probe.naturalWidth;
      this.naturalHeight = probe.naturalHeight;
    };
    probe.src = this.imageDataUrl;
  }

  // ------------------------------------------------------------- edição

  private commit(next: readonly ImageAnnotation[]): void {
    this.history = commitAnnotations(this.history, next);
    this.error = "";
    this.render();
  }

  private newId(): string {
    return `ann-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  private startDraft(x: number, y: number): void {
    if (this.mode === "select") {
      const hit = annotationAt(this.annotations, { x, y });
      this.selectedId = hit?.id ?? null;
      if (hit) this.draft = { annotation: hit, kind: "move", originX: x, originY: y };
      this.render();
      return;
    }
    if (this.mode === "text") return; // texto é por clique, tratado no pointerup

    const base = { id: this.newId(), color: this.color, strokeWidth: this.stroke };
    const annotation: ImageAnnotation = this.mode === "arrow"
      ? { ...base, type: "arrow", x1: x, y1: y, x2: x, y2: y }
      : { ...base, type: this.mode === "ellipse" ? "ellipse" : "rect", x, y, width: 0, height: 0 };
    this.draft = { annotation, kind: "create", originX: x, originY: y };
    this.renderOverlayOnly();
  }

  private updateDraft(x: number, y: number): void {
    if (!this.draft) return;
    const { annotation, kind, originX, originY } = this.draft;

    if (kind === "move") {
      this.draft = { ...this.draft, annotation: moveAnnotation(annotation, x - originX, y - originY), originX: x, originY: y };
    } else if (annotation.type === "arrow") {
      this.draft = { ...this.draft, annotation: { ...annotation, x2: x, y2: y } };
    } else if (annotation.type !== "text") {
      this.draft = { ...this.draft, annotation: { ...annotation, width: x - originX, height: y - originY } };
    }
    this.renderOverlayOnly();
  }

  private endDraft(): void {
    if (!this.draft) return;
    const { annotation, kind } = this.draft;
    this.draft = null;
    try {
      const normalized = normalizeAnnotation(annotation);
      const others = this.history.present.filter(a => a.id !== normalized.id);
      this.commit(kind === "create" ? [...others, normalized] : this.history.present.map(a => (a.id === normalized.id ? normalized : a)));
      if (kind === "create") this.selectedId = normalized.id;
    } catch (error) {
      // Arraste curto demais: descarta o rascunho e explica, sem gravar
      // uma marcação degenerada nem travar o editor.
      this.error = error instanceof AnnotationRuleError ? error.message : "Não foi possível criar a marcação.";
    }
    this.render();
  }

  private addText(x: number, y: number): void {
    const text = prompt("Texto da marcação:", "");
    if (text === null) return;
    try {
      const annotation = normalizeAnnotation({
        id: this.newId(), type: "text", x, y, text, color: this.color, fontSize: this.fontSize
      });
      this.commit([...this.history.present, annotation]);
      this.selectedId = annotation.id;
      this.render();
    } catch (error) {
      this.error = error instanceof AnnotationRuleError ? error.message : "Texto inválido.";
      this.render();
    }
  }

  private updateSelected(patch: Partial<ImageAnnotation>): void {
    const selected = this.selected;
    if (!selected) return;
    this.commit(this.history.present.map(a => (a.id === selected.id ? { ...a, ...patch } as ImageAnnotation : a)));
  }

  private deleteSelected(): void {
    if (!this.selectedId) return;
    this.commit(this.history.present.filter(a => a.id !== this.selectedId));
    this.selectedId = null;
    this.render();
  }

  // ------------------------------------------------------------- salvar

  /**
   * Imagem achatada (item 18): a foto original em resolução natural com as
   * marcações desenhadas por cima. Sem redimensionar — o documento técnico
   * precisa da qualidade original (item 26).
   */
  private async flatten(): Promise<string> {
    const image = await this.loadImage(this.imageDataUrl);
    const width = image.naturalWidth || 1200;
    const height = image.naturalHeight || 900;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas indisponível neste navegador.");
    context.drawImage(image, 0, 0, width, height);

    if (this.history.present.length > 0) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">${annotationsToSvgElements(this.history.present, width, height)}</svg>`;
      const overlay = await this.loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      context.drawImage(overlay, 0, 0, width, height);
    }
    return canvas.toDataURL("image/png");
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Não foi possível carregar a imagem."));
      image.src = src;
    });
  }

  private async save(): Promise<void> {
    try {
      const renderedDataUrl = await this.flatten();
      this.dispatchEvent(new CustomEvent("annotation-save", {
        bubbles: true,
        detail: { annotations: this.history.present.map(a => ({ ...a })), renderedDataUrl }
      }));
    } catch (error) {
      this.error = (error as Error)?.message || "Falha ao gerar a imagem editada.";
      this.render();
    }
  }

  private cancel(): void {
    // CANCELAR (item 15): nada é emitido, nada é gravado — quem abriu o
    // editor mantém exatamente o que tinha antes.
    this.dispatchEvent(new CustomEvent("annotation-cancel", { bubbles: true }));
  }

  // ------------------------------------------------------------- render

  private render(): void {
    if (!this.imageDataUrl) { this.innerHTML = ""; return; }
    const selected = this.selected;
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="iae" role="dialog" aria-label="Editor de imagem">
        <div class="iae-bar">
          <div class="iae-group">
            ${TOOLS.map(([id, label]) => `<button type="button" class="iae-tool ${this.mode === id ? "on" : ""}" data-tool="${id}">${label}</button>`).join("")}
          </div>
          <div class="iae-group">
            ${(Object.keys(ANNOTATION_COLORS) as AnnotationColorName[]).map(name =>
              `<button type="button" class="iae-color ${this.color === name ? "on" : ""}" data-color="${name}" title="${name}" style="background:${ANNOTATION_COLORS[name]}"></button>`).join("")}
          </div>
          <div class="iae-group">
            <select class="iae-select" id="iae-stroke" title="Espessura">
              ${(Object.keys(STROKE_WIDTHS) as StrokeWidthName[]).map(name => `<option value="${name}" ${this.stroke === name ? "selected" : ""}>${name}</option>`).join("")}
            </select>
            <select class="iae-select" id="iae-font" title="Tamanho do texto">
              ${(Object.keys(FONT_SIZES) as FontSizeName[]).map(name => `<option value="${name}" ${this.fontSize === name ? "selected" : ""}>${name}</option>`).join("")}
            </select>
          </div>
          <div class="iae-group">
            <button type="button" class="iae-btn" id="iae-undo" ${canUndo(this.history) ? "" : "disabled"} title="Desfazer">DESFAZER</button>
            <button type="button" class="iae-btn" id="iae-redo" ${canRedo(this.history) ? "" : "disabled"} title="Refazer">REFAZER</button>
            <button type="button" class="iae-btn" id="iae-del" ${selected ? "" : "disabled"} title="Excluir selecionado">EXCLUIR</button>
            <button type="button" class="iae-btn" id="iae-clear" ${this.history.present.length > 0 ? "" : "disabled"}>LIMPAR</button>
          </div>
          <div class="iae-group iae-zoom">
            <button type="button" class="iae-btn" id="iae-zoom-out" title="Reduzir">−</button>
            <button type="button" class="iae-btn" id="iae-zoom-fit" title="Ajustar à tela">AJUSTAR</button>
            <button type="button" class="iae-btn" id="iae-zoom-in" title="Ampliar">+</button>
          </div>
        </div>

        ${this.error ? `<div class="iae-error">${this.esc(this.error)}</div>` : ""}

        <div class="iae-stage">
          <div class="iae-canvas" id="iae-canvas" style="width:${Math.round(this.zoom * 100)}%">
            <img class="iae-img" src="${this.esc(this.imageDataUrl)}" alt="Imagem em edição" draggable="false">
            <svg class="iae-layer" viewBox="0 0 1000 1000" preserveAspectRatio="none">${annotationsToSvgElements(this.annotations, 1000, 1000)}</svg>
            <svg class="iae-ui" viewBox="0 0 1000 1000" preserveAspectRatio="none">${this.selectionMarkupOf(selected)}</svg>
          </div>
        </div>

        <div class="iae-foot">
          <span class="iae-hint">${this.hintOf()}</span>
          <div class="iae-group">
            <button type="button" class="iae-btn" id="iae-cancel">CANCELAR</button>
            <button type="button" class="iae-btn primary" id="iae-save">SALVAR EDIÇÃO</button>
          </div>
        </div>
      </div>`;
    this.bind();
  }

  /** Redesenha só a camada de marcações — arrastar não pode reconstruir a tela inteira. */
  private renderOverlayOnly(): void {
    const layer = this.querySelector(".iae-layer");
    if (layer) layer.innerHTML = annotationsToSvgElements(this.annotations, 1000, 1000);
  }

  /** Caixa de seleção e handles — só UI, nunca entram na imagem achatada. */
  private selectionMarkupOf(selected: ImageAnnotation | null): string {
    if (!selected) return "";
    const bounds = boundsOf(selected);
    const x = bounds.x * 1000, y = bounds.y * 1000, w = bounds.width * 1000, h = bounds.height * 1000;
    const handle = (hx: number, hy: number) => `<rect class="h" x="${hx - 9}" y="${hy - 9}" width="18" height="18" rx="3"/>`;
    return `<rect class="sel" x="${x}" y="${y}" width="${w}" height="${h}"/>${handle(x + w, y + h)}`;
  }

  private hintOf(): string {
    if (this.mode === "select") return "Toque numa marcação para selecionar; arraste para mover. Use o canto para redimensionar.";
    if (this.mode === "text") return "Toque sobre a imagem para inserir o texto.";
    return "Arraste sobre a imagem para desenhar a marcação.";
  }

  // ------------------------------------------------------------- eventos

  private pointOf(event: PointerEvent): { x: number; y: number } {
    const canvas = this.querySelector("#iae-canvas") as HTMLElement | null;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: rect.width === 0 ? 0 : (event.clientX - rect.left) / rect.width,
      y: rect.height === 0 ? 0 : (event.clientY - rect.top) / rect.height
    };
  }

  private bind(): void {
    this.querySelectorAll("[data-tool]").forEach(button => button.addEventListener("click", () => {
      this.mode = (button as HTMLElement).dataset["tool"] as Mode;
      if (this.mode !== "select") this.selectedId = null;
      this.render();
    }));

    this.querySelectorAll("[data-color]").forEach(button => button.addEventListener("click", () => {
      this.color = (button as HTMLElement).dataset["color"] as AnnotationColorName;
      // Com algo selecionado, a escolha recolore a marcação (item 11).
      if (this.selected) this.updateSelected({ color: this.color } as Partial<ImageAnnotation>);
      else this.render();
    }));

    const stroke = this.querySelector("#iae-stroke") as HTMLSelectElement | null;
    stroke?.addEventListener("change", () => {
      this.stroke = stroke.value as StrokeWidthName;
      const selected = this.selected;
      if (selected && selected.type !== "text") this.updateSelected({ strokeWidth: this.stroke } as Partial<ImageAnnotation>);
      else this.render();
    });

    const font = this.querySelector("#iae-font") as HTMLSelectElement | null;
    font?.addEventListener("change", () => {
      this.fontSize = font.value as FontSizeName;
      const selected = this.selected;
      if (selected && selected.type === "text") this.updateSelected({ fontSize: this.fontSize } as Partial<ImageAnnotation>);
      else this.render();
    });

    this.querySelector("#iae-undo")?.addEventListener("click", () => { this.history = undo(this.history); this.selectedId = null; this.render(); });
    this.querySelector("#iae-redo")?.addEventListener("click", () => { this.history = redo(this.history); this.selectedId = null; this.render(); });
    this.querySelector("#iae-del")?.addEventListener("click", () => this.deleteSelected());
    this.querySelector("#iae-clear")?.addEventListener("click", () => {
      if (!confirm("Remover todas as marcações desta imagem? A foto original não é alterada.")) return;
      this.history = clearAnnotations(this.history);
      this.selectedId = null;
      this.render();
    });

    this.querySelector("#iae-zoom-in")?.addEventListener("click", () => { this.zoom = Math.min(4, this.zoom + 0.25); this.render(); });
    this.querySelector("#iae-zoom-out")?.addEventListener("click", () => { this.zoom = Math.max(0.5, this.zoom - 0.25); this.render(); });
    this.querySelector("#iae-zoom-fit")?.addEventListener("click", () => { this.zoom = 1; this.render(); });

    this.querySelector("#iae-cancel")?.addEventListener("click", () => this.cancel());
    this.querySelector("#iae-save")?.addEventListener("click", () => void this.save());

    const canvas = this.querySelector("#iae-canvas") as HTMLElement | null;
    if (!canvas) return;
    canvas.addEventListener("pointerdown", event => {
      event.preventDefault();
      const point = this.pointOf(event);
      if (this.mode === "text") return;
      canvas.setPointerCapture(event.pointerId);
      this.startDraft(point.x, point.y);
    });
    canvas.addEventListener("pointermove", event => {
      if (!this.draft) return;
      event.preventDefault();
      const point = this.pointOf(event);
      this.updateDraft(point.x, point.y);
    });
    const finish = (event: PointerEvent) => {
      if (this.mode === "text") {
        const point = this.pointOf(event);
        this.addText(point.x, point.y);
        return;
      }
      if (this.draft) this.endDraft();
    };
    canvas.addEventListener("pointerup", finish);
    canvas.addEventListener("pointercancel", () => { this.draft = null; this.render(); });
  }

  private esc(value: unknown): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .iae { display: flex; flex-direction: column; gap: 8px; background: var(--color-surface-secondary, #18181b);
             border: 1px solid var(--color-graphite-300, #3f3f46); border-radius: 8px; padding: 10px; }
      .iae-bar, .iae-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
      .iae-foot { justify-content: space-between; border-top: 1px solid var(--color-graphite-300, #3f3f46); padding-top: 8px; }
      .iae-group { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
      .iae-tool, .iae-btn { padding: 7px 10px; border-radius: 6px; border: 1px solid var(--color-graphite-300, #3f3f46);
                            background: transparent; color: var(--color-text-primary, #fafafa); font-size: 0.68rem;
                            font-weight: 600; letter-spacing: 0.04em; cursor: pointer; min-height: 36px; }
      .iae-tool.on { background: var(--color-primary, #2563eb); border-color: var(--color-primary, #2563eb); color: var(--ia-fg, #fff); }
      .iae-btn[disabled] { opacity: 0.4; cursor: not-allowed; }
      .iae-btn.primary { background: var(--color-primary, #2563eb); border-color: var(--color-primary, #2563eb); color: var(--ia-fg, #fff); }
      .iae-color { width: 26px; height: 26px; min-height: 26px; border-radius: 50%; border: 2px solid transparent; cursor: pointer; padding: 0; }
      .iae-color.on { border-color: var(--color-text-primary, #fafafa); box-shadow: 0 0 0 2px rgba(255,255,255,0.25); }
      .iae-select { padding: 6px 8px; border-radius: 6px; border: 1px solid var(--color-graphite-300, #3f3f46);
                    background: var(--color-input-bg, #27272a); color: var(--color-text-primary, #fafafa); font-size: 0.7rem; min-height: 36px; }
      .iae-error { padding: 7px 10px; border-radius: 6px; background: rgba(239,68,68,0.15); color: var(--ia-danger-fg, #fca5a5); font-size: 0.75rem; }

      .iae-stage { overflow: auto; max-height: 62vh; background: var(--ia-bg-inset, #0b0b0d); border-radius: 6px; display: flex; justify-content: center; }
      .iae-canvas { position: relative; flex: none; touch-action: none; user-select: none; }
      .iae-img { display: block; width: 100%; height: auto; pointer-events: none; }
      .iae-layer, .iae-ui { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
      .iae-ui .sel { fill: none; stroke: #ffffff; stroke-width: 3; stroke-dasharray: 10 8; }
      .iae-ui .h { fill: #ffffff; stroke: #000000; stroke-width: 2; }
      .iae-hint { font-size: 0.7rem; color: var(--color-text-disabled, #a1a1aa); }

      /* Campo: no celular a barra rola horizontalmente e os alvos de toque
         crescem — nada depende de hover nem de clique com o botão direito. */
      @media (max-width: 640px) {
        .iae-bar { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; }
        .iae-group { flex-wrap: nowrap; }
        .iae-tool, .iae-btn { min-height: 42px; padding: 9px 12px; }
        .iae-color { width: 32px; height: 32px; min-height: 32px; }
        .iae-stage { max-height: 50vh; }
        .iae-hint { display: none; }
        .iae-foot .iae-btn { flex: 1; }
      }`;
  }
}

if (!customElements.get("app-image-annotation-editor")) {
  customElements.define("app-image-annotation-editor", AppImageAnnotationEditor);
}
